import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import CustomerCombobox from '../../components/CustomerCombobox';
import MasterCombobox from '../../components/MasterCombobox';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Truck, MapPin, Wrench, Phone, ArrowRight, CheckCircle2, Package, Scale, Ruler, Trash2, Plus, ExternalLink, Building2, RefreshCw, Eye, Edit2, Calendar, FileText } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BookingDispatchBoard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({ weight: '', eway_bill: '', advance: '', selected_vehicle: null, selected_driver: '', selected_vendor: '' });
  const [truckAssignments, setTruckAssignments] = useState([
    { slot: 1, requested_size: '', selected_vendor: '', selected_vehicle: null, selected_driver: '', weight: '', eway_bill: '', advance: '' }
  ]);
  const [activeSlotIndex, setActiveSlotIndex] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);


  const [showNewBookingModal, setShowNewBookingModal] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const isSuperAdmin = (() => { try { const p = JSON.parse(localStorage.getItem('user_profile') || '{}'); return p.role === 'super_admin'; } catch { return false; } })();
  
  const [newBooking, setNewBooking] = useState({
    company_id: '',
    inquiry_date: new Date().toISOString().split('T')[0],
    booking_date: new Date().toISOString().split('T')[0],
    customer_id: '', customer_name: '',
    customer_branch_id: '', customer_branch_name: '', branch_address: '',
    booking_type: 'Online',
    from_location_id: '', from_location_name: '',
    to_location_id: '', to_location_name: '',
    vehicle_type_id: '', vehicle_type_name: '',
    vehicle_size: '',
    no_of_vehicles: 1,
    weight: '',
    gross_weight: '',
    packages: '',
    dimensions: '',
    dimensions_unit: 'CM',
    load_type: '', trip: '', trip_type: '',
    vehicle_ownership: '', booking_date: '', inquiry_type: '', remarks: ''
  });

  // Calculate available sizes dynamically (selected vehicle type's sizes, or all master sizes as fallback)
  const availableSizes = useMemo(() => {
    if (newBooking.vehicle_type_id) {
      const vt = vehicleTypes.find(x => x.id === newBooking.vehicle_type_id);
      if (vt?.sizes?.length > 0) return vt.sizes;
    }
    // Fallback: all unique sizes across all vehicle types
    const map = new Map();
    vehicleTypes.forEach(vt => {
      (vt.sizes || []).forEach(s => {
        if (s.size_name && !map.has(s.size_name)) {
          map.set(s.size_name, s);
        }
      });
    });
    return Array.from(map.values());
  }, [vehicleTypes, newBooking.vehicle_type_id]);

  // Multiple truck capacities management (when no_of_vehicles >= 1)
  const [truckSizes, setTruckSizes] = useState(['']);

  const handleNoOfTrucksChange = (val) => {
    const num = Math.max(1, parseInt(val, 10) || 1);
    setTruckSizes(prev => {
      const updated = [...prev];
      if (num > updated.length) {
        while (updated.length < num) {
          updated.push(updated[0] || '');
        }
      } else if (num < updated.length) {
        updated.splice(num);
      }
      const combined = updated.filter(Boolean).join(', ');
      setNewBooking(b => ({ ...b, no_of_vehicles: num, vehicle_size: combined }));
      return updated;
    });
  };

  const handleTruckSizeChange = (index, sizeValue) => {
    setTruckSizes(prev => {
      const updated = [...prev];
      updated[index] = sizeValue;
      const combined = updated.filter(Boolean).join(', ');
      setNewBooking(b => ({ ...b, vehicle_size: combined }));
      return updated;
    });
  };

  const handleCopyFirstSizeToAll = () => {
    const first = truckSizes[0];
    if (!first) return;
    setTruckSizes(prev => {
      const updated = prev.map(() => first);
      const combined = updated.filter(Boolean).join(', ');
      setNewBooking(b => ({ ...b, vehicle_size: combined }));
      return updated;
    });
  };

  // Multiple package items management
  const [packageItems, setPackageItems] = useState([
    { id: 1, packages: '', gross_weight: '', dimensions: '', unit: 'CM' }
  ]);

  const handleAddPackageRow = () => {
    setPackageItems(prev => [
      ...prev,
      { id: Date.now(), packages: '', gross_weight: '', dimensions: '', unit: 'CM' }
    ]);
  };

  const handleRemovePackageRow = (index) => {
    setPackageItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePackageItem = (index, field, value) => {
    setPackageItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const totalPackages = useMemo(() => {
    return packageItems.reduce((sum, item) => sum + (parseInt(item.packages, 10) || 0), 0);
  }, [packageItems]);

  const totalGrossWeight = useMemo(() => {
    return packageItems.reduce((sum, item) => sum + (parseFloat(item.gross_weight) || 0), 0);
  }, [packageItems]);

  const fetchBranches = async (custId) => {
    try {
      const res = await fetch(`${backendUrl}/api/customers/${custId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const bList = data.branches || [];
        setBranches(bList);
        if (bList.length > 0) {
          const defaultBranch = bList.find(b => b.is_head_office) || bList[0];
          const addr = [defaultBranch.address_line_1, defaultBranch.address_line_2, defaultBranch.city, defaultBranch.state, defaultBranch.pincode, defaultBranch.country].filter(Boolean).join(', ');
          setNewBooking(prev => ({
            ...prev,
            customer_branch_id: defaultBranch.id,
            customer_branch_name: defaultBranch.branch_nick_name || '',
            branch_address: addr
          }));
        }
      }
    } catch (e) { console.error(e); }
  };

  const loadBookingMasters = useCallback(async () => {
    // 1. Customers
    try {
      const cRes = await fetch(`${backendUrl}/api/customers?limit=500`, { headers: getAuthHeaders() });
      if (cRes.ok) {
        const d = await cRes.json();
        setCustomers(d.items || d || []);
      }
    } catch (e) {
      console.error('Failed to load customers:', e);
    }

    // 2. Companies (Super Admin selection)
    try {
      const compRes = await fetch(`${backendUrl}/api/companies`, { headers: getAuthHeaders() });
      if (compRes.ok) {
        const d = await compRes.json();
        const compList = d.items || d || [];
        setCompanies(compList);
        if (compList.length > 0) {
          setSelectedCompanyId(prev => prev || compList[0].id);
        }
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    }

    // 3. Locations
    try {
      const lRes = await fetch(`${backendUrl}/api/locations?limit=500`, { headers: getAuthHeaders() });
      if (lRes.ok) {
        const d = await lRes.json();
        const raw = d.items || d || [];
        setLocations(raw.map(l => ({
          ...l,
          name: `${l.location_name || l.name || ''}${l.location_code ? ' (' + l.location_code + ')' : ''}`,
          subtext: [l.city, l.country].filter(Boolean).join(', ')
        })));
      }
    } catch (e) {
      console.error('Failed to load locations:', e);
    }

    // 4. Vehicle Types
    try {
      const vRes = await fetch(`${backendUrl}/api/vehicle-types`, { headers: getAuthHeaders() });
      if (vRes.ok) {
        const d = await vRes.json();
        const raw = d.items || d || [];
        setVehicleTypes(raw.map(v => ({ ...v, name: v.type_name || v.name || '' })));
      }
    } catch (e) {
      console.error('Failed to load vehicle types:', e);
    }
  }, [backendUrl, getAuthHeaders]);

  // Load masters eagerly on mount so data is instantly available when modal opens
  useEffect(() => {
    loadBookingMasters();
  }, [loadBookingMasters]);

  // Also reload if modal opens and data is not yet loaded
  useEffect(() => {
    if (showNewBookingModal && (customers.length === 0 || companies.length === 0)) {
      loadBookingMasters();
    }
  }, [showNewBookingModal, customers.length, companies.length, loadBookingMasters]);

  const handleSaveBooking = async () => {
    if (!newBooking.customer_id || !newBooking.from_location_id || !newBooking.to_location_id) {
      toast({ title: 'Error', description: 'Customer, From, and To locations are required.', variant: 'destructive' });
      return;
    }
    setSavingBooking(true);
    try {
      const payload = { ...newBooking };
      if (selectedCompanyId) payload.company_id = selectedCompanyId;

      // Integrate multiple package items if defined
      const validItems = packageItems.filter(p => p.packages || p.gross_weight || p.dimensions);
      if (validItems.length > 0) {
        payload.package_items = validItems;
        if (totalPackages > 0) payload.packages = String(totalPackages);
        if (totalGrossWeight > 0) {
          payload.gross_weight = String(totalGrossWeight);
          payload.weight = String(totalGrossWeight);
        }
        const dimSummary = validItems
          .filter(p => p.dimensions)
          .map(p => `${p.packages ? p.packages + ' pkgs: ' : ''}${p.dimensions} ${p.unit}`)
          .join('; ');
        if (dimSummary) payload.dimensions = dimSummary;
      } else {
        if (!payload.weight && payload.gross_weight) {
          payload.weight = payload.gross_weight;
        }
        if (payload.dimensions && payload.dimensions_unit) {
          payload.dimensions = `${payload.dimensions} (${payload.dimensions_unit})`;
        }
      }
      
      // Clean up empty strings that cause Pydantic 422 errors (especially for dates and numbers)
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          payload[key] = null;
        }
      });
      const bRes = await fetch(`${backendUrl}/api/booking-requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (bRes.ok) {
        toast({ title: 'Success', description: 'Booking created successfully!' });
        setShowNewBookingModal(false);
        setPackageItems([
          { id: 1, packages: '', gross_weight: '', dimensions: '', unit: 'CM' }
        ]);
        setTruckSizes(['']);
        setNewBooking({
          company_id: '',
          inquiry_date: new Date().toISOString().split('T')[0],
          booking_date: new Date().toISOString().split('T')[0],
          customer_id: '', customer_name: '',
          customer_branch_id: '', customer_branch_name: '', branch_address: '',
          booking_type: 'Online',
          from_location_id: '', from_location_name: '',
          to_location_id: '', to_location_name: '',
          vehicle_type_id: '', vehicle_type_name: '',
          vehicle_size: '',
          no_of_vehicles: 1,
          weight: '', gross_weight: '', packages: '', dimensions: '', dimensions_unit: 'CM',
          load_type: '', trip: '', trip_type: '',
          vehicle_ownership: '', booking_date: '', inquiry_type: '', remarks: ''
        });
        loadData(); // reload board
      } else {
        toast({ title: 'Error', description: 'Failed to create booking.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Server error.', variant: 'destructive' });
    }
    setSavingBooking(false);
  };

  const handleConfirmDispatch = async () => {
    // Validation: Vehicle required for all. Driver required only for OWN fleet.
    const unassigned = truckAssignments.find(t => {
      if (!t.selected_vehicle) return true;
      if (t.fleet_type !== 'VENDOR' && !t.selected_driver) return true;
      return false;
    });

    if (unassigned) {
      if (!unassigned.selected_vehicle) {
        toast({ 
          title: 'Vehicle Required', 
          description: `Please select a vehicle for Truck #${unassigned.slot} (${unassigned.requested_size || 'Requested Truck'}).`, 
          variant: 'destructive' 
        });
      } else {
        toast({ 
          title: 'Driver Required', 
          description: `Please select a driver for Truck #${unassigned.slot} (Own Fleet requires an assigned driver).`, 
          variant: 'destructive' 
        });
      }
      return;
    }
    
    try {
      const payload = {
        trucks: truckAssignments.map(t => ({
          selected_vehicle: t.selected_vehicle,
          selected_driver: t.selected_driver || null,
          selected_vendor: t.selected_vendor || null,
          fleet_type: t.fleet_type || 'OWN',
          weight: t.weight ? parseFloat(t.weight) : null,
          eway_bill: t.eway_bill || null,
          advance: (t.fleet_type !== 'VENDOR' && t.advance) ? parseFloat(t.advance) : null,
          vehicle_size: t.requested_size || null
        }))
      };
      const res = await fetch(`${backendUrl}/api/booking-requests/${selectedBooking.id}/dispatch`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const hasOwn = truckAssignments.some(t => t.fleet_type !== 'VENDOR');
        const hasVendor = truckAssignments.some(t => t.fleet_type === 'VENDOR');
        let desc = `Created ${truckAssignments.length} separate ${truckAssignments.length > 1 ? 'Dockets' : 'Docket'}.`;
        if (hasOwn) desc += ` Trips generated for Own Fleet trucks.`;
        if (hasVendor && !hasOwn) desc += ` Trip creation skipped for Vendor Fleet.`;
        toast({ 
          title: 'Booking Dispatched Successfully!', 
          description: desc
        });
        setShowDispatchModal(false);
        loadData(); // reload the board to update status
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: 'Error', description: err.detail || 'Failed to dispatch booking.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Server error.', variant: 'destructive' });
    }
  };


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Fleet Status
      const fRes = await fetch(`${backendUrl}/api/fleet-live-status`, { headers: getAuthHeaders() });
      if (fRes.ok) {
        setFleet(await fRes.json());
      }
      
      // 2. Fetch Pending Bookings
      const bRes = await fetch(`${backendUrl}/api/booking-requests?status=PENDING`, { headers: getAuthHeaders() });
      if (bRes.ok) {
        const bData = await bRes.json();
        setBookings(bData.items || []);
      }

      // 3. Fetch Available Drivers (not on active trips)
      const dRes = await fetch(`${backendUrl}/api/available-drivers`, { headers: getAuthHeaders() });
      if (dRes.ok) {
        setAvailableDrivers(await dRes.json());
      }

      // 4. Fetch Vendors for fleet filtering & assignment
      const vRes = await fetch(`${backendUrl}/api/vendors?limit=500`, { headers: getAuthHeaders() });
      if (vRes.ok) {
        const vData = await vRes.json();
        setVendors(vData.items || vData || []);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [backendUrl, getAuthHeaders]);

  const refreshVendorsAndFleet = async () => {
    setRefreshing(true);
    try {
      const [fRes, vRes, dRes] = await Promise.all([
        fetch(`${backendUrl}/api/fleet-live-status`, { headers: getAuthHeaders() }),
        fetch(`${backendUrl}/api/vendors?limit=500`, { headers: getAuthHeaders() }),
        fetch(`${backendUrl}/api/available-drivers`, { headers: getAuthHeaders() })
      ]);
      if (fRes.ok) setFleet(await fRes.json());
      if (vRes.ok) {
        const vData = await vRes.json();
        setVendors(vData.items || vData || []);
      }
      if (dRes.ok) setAvailableDrivers(await dRes.json());
      toast({ title: 'Refreshed', description: 'Vendors and fleet live status updated.' });
    } catch (e) {
      console.error(e);
    }
    setRefreshing(false);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const [showAllTrucks, setShowAllTrucks] = useState(false);
  const [showAllDrivers, setShowAllDrivers] = useState(false);

  const availableFleet = fleet.filter(f => f.status === 'AVAILABLE');
  const onTripFleet = fleet.filter(f => f.status === 'ON_TRIP');

  const currentSlot = truckAssignments[activeSlotIndex] || truckAssignments[0] || {
    slot: 1, requested_size: '', fleet_type: 'OWN', selected_vendor: '', selected_vehicle: null, selected_driver: '', weight: '', eway_bill: '', advance: ''
  };

  const updateCurrentSlot = (fields) => {
    setTruckAssignments(prev => {
      const next = [...prev];
      if (next[activeSlotIndex]) {
        next[activeSlotIndex] = { ...next[activeSlotIndex], ...fields };
      }
      return next;
    });
  };

  // Vehicles already assigned to other slots in this dispatch session
  const alreadySelectedVehicleIds = useMemo(() => {
    return truckAssignments
      .filter((_, idx) => idx !== activeSlotIndex)
      .map(t => t.selected_vehicle)
      .filter(Boolean);
  }, [truckAssignments, activeSlotIndex]);

  // Filter available trucks by active slot's selected vendor
  const vendorFilteredFleet = useMemo(() => {
    if (!currentSlot.selected_vendor) return availableFleet;
    return availableFleet.filter(f => f.vendor_id === currentSlot.selected_vendor);
  }, [availableFleet, currentSlot.selected_vendor]);

  // Filter available trucks matching the active slot's requested size & booking vehicle type
  const matchingFleet = useMemo(() => {
    if (!selectedBooking) return vendorFilteredFleet;
    
    const reqTypeId = selectedBooking.vehicle_type_id;
    const reqTypeName = selectedBooking.vehicle_type_name?.toLowerCase()?.trim();
    const reqSize = currentSlot.requested_size?.toLowerCase()?.replace(/\s+/g, '')?.trim();

    if (!reqTypeId && !reqTypeName && !reqSize) return vendorFilteredFleet;

    return vendorFilteredFleet.filter(f => {
      // 1. Vehicle Type check (if specified)
      if (reqTypeId && f.vehicle_type_id) {
        if (f.vehicle_type_id !== reqTypeId) return false;
      } else if (reqTypeName && f.vehicle_type_name) {
        const truckTypeName = f.vehicle_type_name.toLowerCase().trim();
        if (!truckTypeName.includes(reqTypeName) && !reqTypeName.includes(truckTypeName)) {
          return false;
        }
      }

      // 2. Specific Slot Size check (e.g. "22 FT" for Truck 1, "12 FT" for Truck 2, "32 FT" for Truck 3)
      if (reqSize && f.vehicle_size) {
        const truckSize = f.vehicle_size.toLowerCase().replace(/\s+/g, '').trim();
        if (!truckSize.includes(reqSize) && !reqSize.includes(truckSize)) {
          return false;
        }
      }

      return true;
    });
  }, [vendorFilteredFleet, currentSlot.requested_size, selectedBooking]);

  const displayedFleet = showAllTrucks ? vendorFilteredFleet : (matchingFleet.length > 0 ? matchingFleet : vendorFilteredFleet);

  const filteredDrivers = useMemo(() => {
    if (showAllDrivers || !currentSlot.selected_vendor) {
      return availableDrivers;
    }
    const vendorDrivers = availableDrivers.filter(d => d.vendor_id === currentSlot.selected_vendor);
    return vendorDrivers;
  }, [availableDrivers, currentSlot.selected_vendor, showAllDrivers]);

  const openDispatchWizard = (b) => {
    setSelectedBooking(b);
    setShowAllTrucks(false);
    setShowAllDrivers(false);

    const count = Math.max(1, parseInt(b.no_of_vehicles, 10) || 1);
    const rawSizes = (b.vehicle_size || '')
      .split(/[,+/;]/)
      .map(s => s.trim())
      .filter(Boolean);

    let computedWeight = b.gross_weight || b.weight || '';
    if (!computedWeight && Array.isArray(b.package_items) && b.package_items.length > 0) {
      const s = b.package_items.reduce((acc, curr) => acc + (parseFloat(curr.gross_weight) || 0), 0);
      if (s > 0) computedWeight = String(s);
    }

    const perTruckWeight = computedWeight 
      ? String(Math.round((parseFloat(computedWeight) / count) * 100) / 100) 
      : '';

    const initialAssignments = [];
    for (let i = 0; i < count; i++) {
      initialAssignments.push({
        slot: i + 1,
        requested_size: rawSizes[i] || rawSizes[0] || '',
        fleet_type: 'OWN', // 'OWN' (Creates Trip + Docket) or 'VENDOR' (Docket Only)
        selected_vendor: '',
        selected_vehicle: null,
        selected_driver: '',
        weight: perTruckWeight,
        eway_bill: '',
        advance: ''
      });
    }

    setTruckAssignments(initialAssignments);
    setActiveSlotIndex(0);
    setViewingBooking(null);
    setShowDispatchModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Dispatch Control Tower</h1>
          <p className="text-text-secondary font-ibm mt-0.5">Manage pending bookings and live fleet availability</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT COLUMN: BOOKINGS */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold font-manrope flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Pending Bookings
            </h2>
            <Button onClick={() => setShowNewBookingModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-ibm px-3 h-8">
              + New Booking
            </Button>
          </div>
          
                    {bookings.map(b => (
            <Card key={b.id} className="p-4 border border-blue-100 bg-white shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-2.5">
                <div className="min-w-0 pr-2">
                  <div className="font-bold font-ibm text-text-dark text-sm truncate">{b.customer_name || 'Walk-in Customer'}</div>
                  {b.inquiry_number && (
                    <div className="text-[11px] font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 inline-block mt-0.5 font-medium">
                      {b.inquiry_number}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Pending</Badge>
                  <button
                    type="button"
                    onClick={() => setViewingBooking(b)}
                    className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-600 hover:text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer"
                    title="View Full Booking Details"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/booking/edit/${b.id}`)}
                    className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-600 hover:text-amber-700 hover:bg-amber-50 hover:border-amber-300 transition-colors cursor-pointer"
                    title="Edit Booking Request"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-600 font-ibm mb-2">
                <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="truncate">{b.from_location_name}</span> 
                <ArrowRight className="w-3 h-3 shrink-0 text-gray-400" /> 
                <span className="truncate">{b.to_location_name}</span>
              </div>

              <div className="flex items-center justify-between gap-2 text-xs text-gray-700 font-ibm mb-3 bg-slate-50 p-2 rounded border border-slate-100">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Truck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span className="truncate font-semibold text-gray-800">
                    {b.no_of_vehicles > 1 ? `${b.no_of_vehicles} Trucks: ` : ''}
                    {b.vehicle_size || b.vehicle_type_name || 'Any Truck'}
                  </span>
                </div>
                {(b.gross_weight || b.weight) && (
                  <span className="text-[10px] text-gray-500 shrink-0 font-medium">
                    {b.gross_weight || b.weight} KG
                  </span>
                )}
              </div>

              <Button 
                onClick={() => openDispatchWizard(b)} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-ibm font-bold gap-2 text-xs h-9"
              >
                <CheckCircle2 className="w-4 h-4" /> Dispatch Now
              </Button>
            </Card>
          ))}
        </div>

        {/* RIGHT COLUMN: FLEET GARAGE */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-lg font-bold font-manrope flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Live Fleet Garage
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Available Section */}
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800 font-bold font-ibm flex justify-between">
                <span>Available ({availableFleet.length})</span>
              </div>
              {availableFleet.map(f => (
                <Card key={f.id} className="p-3 border-l-4 border-l-green-500 bg-white shadow-sm flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Truck className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold font-ibm text-gray-800">{f.registration_number}</div>
                    <div className="text-xs text-gray-500 font-ibm">{f.vehicle_size}</div>
                    <div className="text-xs text-green-700 font-ibm mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {f.location_desc}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* On Trip Section */}
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-orange-800 font-bold font-ibm flex justify-between">
                <span>In Transit ({onTripFleet.length})</span>
              </div>
              {onTripFleet.map(f => (
                <Card 
                  key={f.id} 
                  onClick={() => f.trip_id && navigate(`/fleet/trips/view/${f.trip_id}`)}
                  className={`p-3 border-l-4 border-l-orange-400 bg-white shadow-sm flex items-start gap-3 transition-all ${f.trip_id ? 'cursor-pointer hover:shadow-md hover:border-orange-500' : ''}`}
                >
                  <div className="p-2 bg-orange-100 rounded-lg shrink-0">
                    <Truck className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold font-ibm text-gray-800">{f.registration_number}</div>
                      {f.trip_number && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/fleet/trips/view/${f.trip_id}`);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold font-ibm bg-orange-100 text-orange-800 hover:bg-orange-200 border border-orange-300 transition-colors"
                          title="View Trip Details"
                        >
                          <span>{f.trip_number}</span>
                          <ExternalLink className="w-3 h-3 text-orange-700" />
                        </button>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 font-ibm mt-0.5">{f.driver_name}</div>
                    <div className="text-xs text-orange-700 font-ibm mt-1 flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 shrink-0" /> {f.location_desc}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>

      </div>

      <Dialog open={showDispatchModal} onOpenChange={setShowDispatchModal}>
        <DialogContent className="sm:max-w-[780px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope text-xl text-blue-900 border-b pb-2 flex items-center justify-between">
              <span>Dispatch Wizard</span>
              {selectedBooking && (
                <span className="text-xs font-ibm font-normal text-gray-500">
                  Inquiry: <strong className="text-blue-700">{selectedBooking.inquiry_number}</strong> • {selectedBooking.customer_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-5 py-2">

              {/* Multi-Truck Tab Bar (shown when multiple trucks are booked) */}
              {truckAssignments.length > 1 && (
                <div className="bg-slate-100 p-3 rounded-xl border border-slate-200 shadow-inner">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold font-manrope text-slate-800 uppercase tracking-wide">
                        Multi-Truck Fleet Allocation
                      </span>
                      <Badge className="bg-blue-600 text-white text-[11px] font-ibm">
                        {truckAssignments.filter(t => t.selected_vehicle && t.selected_driver).length} of {truckAssignments.length} Trucks Configured
                      </Badge>
                    </div>
                    <span className="text-[11px] text-slate-500 font-ibm">
                      Select each truck slot to assign vendor, truck & driver
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {truckAssignments.map((t, idx) => {
                      const isAssigned = !!(t.selected_vehicle && (t.fleet_type === 'VENDOR' || t.selected_driver));
                      const isActive = activeSlotIndex === idx;
                      const assignedTruck = fleet.find(f => f.id === t.selected_vehicle);
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setActiveSlotIndex(idx);
                            setShowAllTrucks(false);
                            setShowAllDrivers(false);
                          }}
                          className={`p-2.5 rounded-lg border text-left transition-all relative cursor-pointer ${
                            isActive 
                              ? 'bg-blue-600 text-white border-blue-700 shadow-md ring-2 ring-blue-300' 
                              : isAssigned 
                                ? 'bg-emerald-50 text-emerald-950 border-emerald-300 hover:bg-emerald-100'
                                : 'bg-white text-slate-800 border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className={`text-xs font-bold font-ibm ${isActive ? 'text-white' : 'text-slate-800'}`}>
                              Truck #{idx + 1}
                            </span>
                            {isAssigned ? (
                              <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${isActive ? 'text-emerald-200' : 'text-emerald-700'}`}>
                                <CheckCircle2 className="w-3 h-3" /> Ready
                              </span>
                            ) : (
                              <span className={`text-[10px] px-1.5 py-0.2 rounded font-medium ${isActive ? 'bg-blue-700 text-blue-100' : 'bg-amber-100 text-amber-700'}`}>
                                Pending
                              </span>
                            )}
                          </div>
                          <div className={`text-[11px] font-medium mt-0.5 truncate ${isActive ? 'text-blue-100' : 'text-slate-600'}`}>
                            Req: <span className="font-bold">{t.requested_size || 'Any Size'}</span>
                          </div>
                          {assignedTruck && (
                            <div className={`text-[10px] font-bold mt-1 truncate ${isActive ? 'text-yellow-200' : 'text-emerald-800'}`}>
                              🚛 {assignedTruck.registration_number}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 1: Freight Details */}
              <div>
                <h3 className="font-bold font-ibm text-gray-700 mb-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">Step 1</span> 
                    Confirm Freight Details {truckAssignments.length > 1 ? `(Truck #${currentSlot.slot})` : '(Docket)'}
                  </div>
                  {truckAssignments.length > 1 && (
                    <span className="text-[11px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 font-ibm font-medium">
                      Docket #{currentSlot.slot} of {truckAssignments.length}
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3.5 rounded-lg border border-gray-200">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600 font-ibm">Actual Weight (KG)</Label>
                    <Input 
                      type="number" 
                      placeholder="Enter weight for this truck..." 
                      value={currentSlot.weight || ''} 
                      onChange={e => updateCurrentSlot({ weight: e.target.value })} 
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600 font-ibm">E-Way Bill No.</Label>
                    <Input 
                      placeholder="Enter E-Way Bill for this truck..." 
                      value={currentSlot.eway_bill || ''} 
                      onChange={e => updateCurrentSlot({ eway_bill: e.target.value })} 
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Assign Fleet */}
              <div>
                <h3 className="font-bold font-ibm text-gray-700 mb-2.5 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-semibold">Step 2</span> 
                    <span>
                      Assign Fleet & Driver{' '}
                      <span className="text-xs font-normal text-gray-500">
                        {currentSlot.fleet_type === 'VENDOR' ? `(Docket #${currentSlot.slot} - Vendor Fleet)` : `(Trip #${currentSlot.slot} & Docket)`}
                      </span>
                    </span>
                  </div>
                  {currentSlot.requested_size && (
                    <span className="text-[11px] text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 font-ibm font-bold">
                      Target Capacity: {currentSlot.requested_size}
                    </span>
                  )}
                </h3>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-4">
                  
                  {/* Fleet Ownership Selector: Own Fleet vs Vendor Fleet */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <Label className="text-xs font-bold text-gray-800 font-ibm block">
                        Fleet Ownership for Truck #{currentSlot.slot}
                      </Label>
                      <p className="text-[11px] text-gray-500 font-ibm">
                        {currentSlot.fleet_type === 'VENDOR' 
                          ? 'Vendor Fleet: Generates Docket (LR) only; trip creation & driver advance tracking are skipped.'
                          : 'Own Fleet: Generates both official Docket (LR) and operational Trip.'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg border border-gray-200">
                      <button
                        type="button"
                        onClick={() => updateCurrentSlot({ fleet_type: 'OWN' })}
                        className={`px-3 py-1 text-xs font-ibm font-semibold rounded transition-all cursor-pointer ${
                          currentSlot.fleet_type !== 'VENDOR'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Own Fleet
                      </button>
                      <button
                        type="button"
                        onClick={() => updateCurrentSlot({ fleet_type: 'VENDOR' })}
                        className={`px-3 py-1 text-xs font-ibm font-semibold rounded transition-all cursor-pointer ${
                          currentSlot.fleet_type === 'VENDOR'
                            ? 'bg-amber-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Vendor Fleet
                      </button>
                    </div>
                  </div>
                  
                  {/* Vendor Selection & Quick Add for this slot */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm space-y-2.5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label className="text-xs font-bold text-gray-800 font-ibm flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
                        Vendor for Truck #{currentSlot.slot} <span className="text-gray-400 font-normal text-[11px]">(Filter Fleet)</span>
                      </Label>
                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                          <button
                            type="button"
                            onClick={refreshVendorsAndFleet}
                            disabled={refreshing}
                            className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-blue-600 bg-gray-100 hover:bg-blue-50 px-2 py-1 rounded border border-gray-200 transition-colors font-ibm font-medium cursor-pointer"
                            title="Reload vendors, fleet and drivers"
                          >
                            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin text-blue-600' : ''}`} />
                            <span>Refresh</span>
                          </button>
                          <a
                            href="/fleet/vendors/add"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded border border-blue-200 transition-colors font-ibm font-semibold"
                            title="Open New Vendor form in a new tab"
                          >
                            <Plus className="w-3 h-3 text-blue-600" />
                            <span>Vendor</span>
                            <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                          </a>
                          <a
                            href={`/fleet/vehicles/add${currentSlot.selected_vendor ? `?vendor_id=${currentSlot.selected_vendor}` : ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded border border-emerald-200 transition-colors font-ibm font-semibold"
                            title="Open New Vehicle form in a new tab"
                          >
                            <Plus className="w-3 h-3 text-emerald-600" />
                            <span>Vehicle</span>
                            <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                          </a>
                          <a
                            href={`/fleet/drivers/add${currentSlot.selected_vendor ? `?vendor_id=${currentSlot.selected_vendor}` : ''}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded border border-purple-200 transition-colors font-ibm font-semibold"
                            title="Open New Driver form in a new tab"
                          >
                            <Plus className="w-3 h-3 text-purple-600" />
                            <span>Driver</span>
                            <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                          </a>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={currentSlot.selected_vendor || ''}
                        onChange={(e) => {
                          const vId = e.target.value;
                          const keepTruck = vId ? displayedFleet.some(f => f.id === currentSlot.selected_vehicle && f.vendor_id === vId) : true;
                          updateCurrentSlot({
                            selected_vendor: vId,
                            selected_vehicle: keepTruck ? currentSlot.selected_vehicle : null,
                            selected_driver: ''
                          });
                        }}
                        className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1.5 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 font-ibm"
                      >
                        <option value="">-- All Vendors ({vendors.length} Total) --</option>
                        {vendors.map(v => (
                          <option key={v.id} value={v.id}>
                            {v.business_name} {v.city ? `(${v.city})` : ''}
                          </option>
                        ))}
                      </select>
                      {currentSlot.selected_vendor && (
                        <button
                          type="button"
                          onClick={() => updateCurrentSlot({ selected_vendor: '', selected_vehicle: null, selected_driver: '' })}
                          className="text-xs text-gray-500 hover:text-red-600 px-2.5 py-1.5 rounded border border-gray-200 hover:border-red-300 font-ibm shrink-0"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Visual Fleet Selector for this slot */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-gray-700 font-medium">
                        Select Truck for Slot #{currentSlot.slot} <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {currentSlot.selected_vendor && (
                          <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded border border-purple-200 font-medium font-ibm">
                            Vendor Fleet: {vendorFilteredFleet.length}
                          </span>
                        )}
                        {currentSlot.requested_size && (
                          <>
                            <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200 font-medium font-ibm">
                              Matching {currentSlot.requested_size}: ({matchingFleet.length})
                            </span>
                            <button
                              type="button"
                              onClick={() => setShowAllTrucks(prev => !prev)}
                              className="text-[10px] text-blue-600 hover:text-blue-800 underline font-ibm font-medium cursor-pointer"
                            >
                              {showAllTrucks ? `Show only matching (${matchingFleet.length})` : `Show all (${vendorFilteredFleet.length})`}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 max-h-[160px] overflow-y-auto pr-1">
                      {displayedFleet.length === 0 && (
                        <div className="col-span-2 text-center py-4 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-500 font-ibm">
                          {currentSlot.selected_vendor
                            ? "No available trucks found for the selected vendor."
                            : `No available trucks match ${currentSlot.requested_size || 'criteria'}.`}
                          <div className="mt-2 flex items-center justify-center gap-3">
                            {currentSlot.selected_vendor ? (
                              <a
                                href={`/fleet/vehicles/add?vendor_id=${currentSlot.selected_vendor}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 hover:underline font-semibold text-xs font-ibm inline-flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" /> Add vehicle for this vendor
                                <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            ) : availableFleet.length > 0 ? (
                              <button
                                type="button"
                                onClick={() => setShowAllTrucks(true)}
                                className="text-blue-600 hover:underline font-medium text-xs font-ibm"
                              >
                                Show all {availableFleet.length} other available trucks
                              </button>
                            ) : null}
                          </div>
                        </div>
                      )}
                      {displayedFleet.map(f => {
                        const isMatch = matchingFleet.some(m => m.id === f.id);
                        const isAssignedToOther = alreadySelectedVehicleIds.includes(f.id);
                        const isCurrentSelected = currentSlot.selected_vehicle === f.id;
                        return (
                          <div 
                            key={f.id} 
                            onClick={() => {
                              if (isAssignedToOther) {
                                toast({ 
                                  title: 'Already Allocated', 
                                  description: 'This truck is already assigned to another slot in this dispatch.', 
                                  variant: 'destructive' 
                                });
                                return;
                              }
                              const newVehicle = isCurrentSelected ? null : f.id;
                              let newDriver = currentSlot.selected_driver;
                              if (!isCurrentSelected && f.vendor_id && !currentSlot.selected_driver) {
                                const match = availableDrivers.find(d => d.vendor_id === f.vendor_id);
                                if (match) newDriver = match.id;
                              }
                              const newVendor = (!isCurrentSelected && f.vendor_id) ? f.vendor_id : currentSlot.selected_vendor;
                              updateCurrentSlot({
                                selected_vehicle: newVehicle,
                                selected_driver: newDriver,
                                selected_vendor: newVendor
                              });
                            }}
                            className={`border rounded p-2 flex items-center justify-between gap-2 transition-all ${
                              isAssignedToOther 
                                ? 'opacity-40 bg-gray-100 cursor-not-allowed border-gray-200' 
                                : isCurrentSelected 
                                  ? 'cursor-pointer border-blue-500 bg-blue-50 ring-2 ring-blue-500' 
                                  : 'cursor-pointer border-gray-200 bg-white hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Truck className={`w-5 h-5 shrink-0 ${isCurrentSelected ? 'text-blue-600' : isMatch ? 'text-green-600' : 'text-gray-400'}`} />
                              <div className="min-w-0">
                                <div className="font-bold text-xs font-ibm truncate">{f.registration_number}</div>
                                <div className="text-[10px] text-gray-500 font-ibm truncate">{f.vehicle_type_name || ''} {f.vehicle_size ? `• ${f.vehicle_size}` : ''}</div>
                                {f.vendor_name && (
                                  <div className="text-[9px] text-blue-700 font-medium truncate font-ibm flex items-center gap-0.5 mt-0.5">
                                    <Building2 className="w-2.5 h-2.5 shrink-0 text-blue-500" />
                                    <span>{f.vendor_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {isAssignedToOther && (
                                <span className="text-[9px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-ibm">Used</span>
                              )}
                              {isMatch && !isAssignedToOther && (
                                <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold font-ibm">Match</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Driver Selector for this slot */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Label className="text-xs text-gray-700 font-medium">
                        Select Driver for Slot #{currentSlot.slot}{' '}
                        {currentSlot.fleet_type === 'VENDOR' ? (
                          <span className="text-gray-400 font-normal text-[11px]">(Optional for Vendor Fleet)</span>
                        ) : (
                          <span className="text-red-500">*</span>
                        )}
                      </Label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {currentSlot.selected_vendor && (
                          <button
                            type="button"
                            onClick={() => setShowAllDrivers(prev => !prev)}
                            className={`text-[10px] px-2 py-0.5 rounded border transition-colors font-ibm font-medium cursor-pointer ${
                              showAllDrivers 
                                ? 'bg-blue-100 text-blue-800 border-blue-300' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300'
                            }`}
                            title="Toggle between vendor-only drivers and all market/own drivers"
                          >
                            {showAllDrivers ? 'Showing All Drivers' : 'Show All / Market Drivers'}
                          </button>
                        )}
                        <a
                          href={`/fleet/drivers/add${currentSlot.selected_vendor ? `?vendor_id=${currentSlot.selected_vendor}` : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-purple-700 hover:text-purple-900 font-medium hover:underline font-ibm"
                          title="Open New Driver form in a new tab"
                        >
                          <Plus className="w-2.5 h-2.5" />
                          <span>Add Driver</span>
                          <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                        </a>
                        <span className="text-[11px] text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200 font-medium">
                          {filteredDrivers.length} Available Drivers {(!showAllDrivers && currentSlot.selected_vendor) ? '(Vendor)' : ''}
                        </span>
                      </div>
                    </div>
                    {filteredDrivers.length === 0 ? (
                      <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 font-ibm flex items-center justify-between">
                        <span>No idle drivers currently registered for this vendor.</span>
                        <button
                          type="button"
                          onClick={() => setShowAllDrivers(true)}
                          className="text-blue-600 hover:underline font-medium ml-2 cursor-pointer"
                        >
                          Show Market / Other Drivers
                        </button>
                      </div>
                    ) : (
                      <select
                        value={currentSlot.selected_driver || ''}
                        onChange={e => updateCurrentSlot({ selected_driver: e.target.value })}
                        className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1.5 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 font-ibm"
                      >
                        <option value="">{currentSlot.fleet_type === 'VENDOR' ? '-- No Driver / Vendor Arranged --' : '-- Choose Driver --'}</option>
                        {filteredDrivers.map(d => {
                          const driverVendor = vendors.find(v => v.id === d.vendor_id);
                          const vendorLabel = driverVendor ? ` [${driverVendor.business_name}]` : '';
                          return (
                            <option key={d.id} value={d.id}>
                              {d.name} {d.mobile_1 ? `(${d.mobile_1})` : ''}{showAllDrivers ? vendorLabel : ''}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  </div>

                  {/* Driver Advance for this slot (Only for Own Fleet) */}
                  {currentSlot.fleet_type !== 'VENDOR' ? (
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Driver Starting Advance for Truck #{currentSlot.slot} (Rs.)</Label>
                      <Input 
                        type="number" 
                        placeholder="Advance given for diesel/toll..." 
                        value={currentSlot.advance || ''} 
                        onChange={e => updateCurrentSlot({ advance: e.target.value })} 
                      />
                    </div>
                  ) : (
                    <div className="text-[11px] text-gray-500 bg-gray-100 p-2 rounded border border-gray-200 font-ibm">
                      ℹ️ Driver advance is disabled for Vendor Fleet (handled in Vendor Freight Memo).
                    </div>
                  )}

                </div>
              </div>

            </div>
          )}

          <DialogFooter className="border-t pt-3 flex items-center justify-between sm:justify-between w-full">
            <div className="text-xs text-gray-500 font-ibm">
              {truckAssignments.length > 1 ? (
                <span>
                  Configured: <strong className="text-blue-700">{truckAssignments.filter(t => t.selected_vehicle && (t.fleet_type === 'VENDOR' || t.selected_driver)).length}</strong> of <strong>{truckAssignments.length}</strong> trucks
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowDispatchModal(false)}>Cancel</Button>
              <Button 
                className="bg-blue-600 text-white font-ibm font-bold" 
                disabled={!truckAssignments.every(t => t.selected_vehicle && (t.fleet_type === 'VENDOR' || t.selected_driver))} 
                onClick={handleConfirmDispatch}
              >
                Confirm Dispatch ({truckAssignments.length} {truckAssignments.length > 1 ? 'Dockets' : 'Docket'})
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW BOOKING DETAILS MODAL */}
      <Dialog open={!!viewingBooking} onOpenChange={open => !open && setViewingBooking(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          {viewingBooking && (
            <>
              <DialogHeader className="border-b pb-3">
                <div className="flex items-start justify-between pr-6">
                  <div>
                    <DialogTitle className="font-manrope text-xl text-blue-950 flex items-center gap-2">
                      <span>Booking Inquiry</span>
                      {viewingBooking.inquiry_number && (
                        <span className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                          {viewingBooking.inquiry_number}
                        </span>
                      )}
                    </DialogTitle>
                    <p className="text-xs text-gray-500 font-ibm mt-1">
                      Inquiry Date: <strong className="text-gray-700">{viewingBooking.inquiry_date || 'N/A'}</strong>
                      {viewingBooking.booking_date ? ` • Target: ${viewingBooking.booking_date}` : ''}
                    </p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 uppercase font-semibold">
                    {viewingBooking.status}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="space-y-4 py-2 font-ibm text-xs">
                {/* Section 1: Customer & Branch */}
                <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-gray-800 text-sm">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <span>Customer Information</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pl-5">
                    <div>
                      <span className="text-gray-500 block">Customer Name:</span>
                      <strong className="text-gray-900 text-xs">{viewingBooking.customer_name || 'Walk-in'}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Branch Nickname:</span>
                      <strong className="text-gray-900 text-xs">{viewingBooking.customer_branch_name || 'Main Branch'}</strong>
                    </div>
                    {viewingBooking.branch_address && (
                      <div className="col-span-2">
                        <span className="text-gray-500 block">Branch Address:</span>
                        <span className="text-gray-700">{viewingBooking.branch_address}</span>
                      </div>
                    )}
                    {viewingBooking.contact_name && (
                      <div>
                        <span className="text-gray-500 block">Contact Person:</span>
                        <span className="text-gray-800 font-medium">{viewingBooking.contact_name} {viewingBooking.contact_phone ? `(${viewingBooking.contact_phone})` : ''}</span>
                      </div>
                    )}
                    {viewingBooking.booking_type && (
                      <div>
                        <span className="text-gray-500 block">Booking Type:</span>
                        <span className="text-gray-800 font-medium">{viewingBooking.booking_type}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 2: Route */}
                <div className="bg-blue-50/50 p-3.5 rounded-lg border border-blue-100 space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-blue-900 text-sm">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span>Route & Locations</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pl-5">
                    <div>
                      <span className="text-gray-500 block">Origin (From):</span>
                      <strong className="text-blue-950 font-bold">{viewingBooking.from_location_name}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Destination (To):</span>
                      <strong className="text-blue-950 font-bold">{viewingBooking.to_location_name}</strong>
                    </div>
                  </div>
                </div>

                {/* Section 3: Fleet Requirements */}
                <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-gray-800 text-sm">
                    <Truck className="w-4 h-4 text-emerald-600" />
                    <span>Fleet & Truck Requirements</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 pl-5">
                    <div>
                      <span className="text-gray-500 block">No. of Trucks:</span>
                      <strong className="text-emerald-700 font-bold text-sm">
                        {viewingBooking.no_of_vehicles || 1} {viewingBooking.no_of_vehicles > 1 ? 'Trucks' : 'Truck'}
                      </strong>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500 block">Vehicle Capacities / Sizes:</span>
                      <strong className="text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200 inline-block font-bold">
                        {viewingBooking.vehicle_size || 'Not Specified'}
                      </strong>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Vehicle Type:</span>
                      <span className="text-gray-800 font-medium">{viewingBooking.vehicle_type_name || 'Any'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Load Type:</span>
                      <span className="text-gray-800 font-medium">{viewingBooking.load_type || 'FTL'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Trip Scope:</span>
                      <span className="text-gray-800 font-medium">{viewingBooking.trip || 'National'}</span>
                    </div>
                  </div>
                </div>

                {/* Section 4: Cargo & Dimensions */}
                <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-gray-800 text-sm">
                    <Package className="w-4 h-4 text-purple-600" />
                    <span>Cargo & Package Summary</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 pl-5">
                    <div>
                      <span className="text-gray-500 block">Total Packages:</span>
                      <strong className="text-gray-900">{viewingBooking.packages || '-'}</strong>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Gross Weight:</span>
                      <strong className="text-gray-900">{viewingBooking.gross_weight || viewingBooking.weight || '-'} KG</strong>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Dimensions:</span>
                      <strong className="text-gray-900">{viewingBooking.dimensions || '-'}</strong>
                    </div>
                  </div>

                  {Array.isArray(viewingBooking.package_items) && viewingBooking.package_items.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 pl-5">
                      <span className="text-[11px] font-bold text-gray-600 uppercase block mb-1">Package Breakdown:</span>
                      <div className="space-y-1">
                        {viewingBooking.package_items.map((pi, idx) => (
                          <div key={idx} className="flex items-center justify-between text-[11px] bg-white p-1.5 rounded border border-gray-200">
                            <span>Item #{idx + 1}: <strong>{pi.packages || 0} pkgs</strong></span>
                            <span>Weight: <strong>{pi.gross_weight || 0} KG</strong></span>
                            <span>Dims: <strong>{pi.dimensions ? `${pi.dimensions} ${pi.unit || 'CM'}` : '-'}</strong></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 5: Remarks */}
                {viewingBooking.remarks && (
                  <div className="bg-amber-50/60 p-3 rounded-lg border border-amber-200 text-xs">
                    <span className="font-bold text-amber-900 block mb-0.5">Remarks / Special Instructions:</span>
                    <p className="text-amber-800">{viewingBooking.remarks}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-3 flex items-center justify-between sm:justify-between w-full">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const id = viewingBooking.id;
                    setViewingBooking(null);
                    navigate(`/booking/edit/${id}`);
                  }}
                  className="gap-1.5 font-ibm text-xs"
                >
                  <Edit2 className="w-3.5 h-3.5 text-amber-600" />
                  <span>Edit Booking</span>
                </Button>

                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setViewingBooking(null)} className="text-xs">
                    Close
                  </Button>
                  <Button 
                    onClick={() => openDispatchWizard(viewingBooking)} 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-ibm font-bold gap-1.5 text-xs"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Proceed to Dispatch</span>
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW BOOKING MODAL */}
      <Dialog open={showNewBookingModal} onOpenChange={setShowNewBookingModal}>
        <DialogContent className="sm:max-w-[1150px] w-full max-h-[92vh] overflow-y-auto bg-slate-50">
          <DialogHeader className="bg-white border-b pb-4 mb-4">
            <DialogTitle className="font-manrope text-xl text-blue-900 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              New Booking Request
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-ibm p-2">
            {/* Column 1: Basic Info */}
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 space-y-3.5 h-full">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Customer Details</h3>

                {isSuperAdmin && (
                  <div className="space-y-1.5 bg-blue-50 p-2.5 rounded border border-blue-100 mb-2">
                    <Label className="text-xs text-blue-800 font-bold">Operating Company <span className="text-red-500">*</span></Label>
                    <select
                      value={selectedCompanyId}
                      onChange={e => {
                        setSelectedCompanyId(e.target.value);
                        setNewBooking(prev => ({ ...prev, company_id: e.target.value }));
                      }}
                      className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                    >
                      <option value="">-- Select Company --</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name || c.company_name} {c.code ? `(${c.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Inquiry Date <span className="text-red-500">*</span></Label>
                    <Input type="date" value={newBooking.inquiry_date} onChange={e => setNewBooking({...newBooking, inquiry_date: e.target.value})} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-gray-600">Booking Date <span className="text-red-500">*</span></Label>
                    <Input type="date" value={newBooking.booking_date} onChange={e => setNewBooking({...newBooking, booking_date: e.target.value})} className="h-8 text-sm" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">Customer <span className="text-red-500">*</span></Label>
                  <CustomerCombobox customers={customers} value={newBooking.customer_id} onSelect={id => {
                    const c = customers.find(x => x.id === id);
                    setNewBooking({...newBooking, customer_id: id || '', customer_name: c?.business_name || c?.name || '', customer_branch_id: '', customer_branch_name: '', branch_address: ''});
                    if (id) fetchBranches(id);
                    else setBranches([]);
                  }} placeholder="Search Customer..." />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">Customer Branch</Label>
                  <MasterCombobox
                    items={branches}
                    value={newBooking.customer_branch_id || ''}
                    onSelect={item => {
                      const br = branches.find(b => b.id === item);
                      const addr = br ? [br.address_line_1, br.address_line_2, br.city, br.state, br.pincode, br.country].filter(Boolean).join(', ') : '';
                      setNewBooking({
                        ...newBooking,
                        customer_branch_id: item || '',
                        customer_branch_name: br?.branch_nick_name || '',
                        branch_address: addr
                      });
                    }}
                    displayField="branch_nick_name"
                    subtextField="city"
                    placeholder={branches.length === 0 ? 'Select a customer first...' : 'Search Branch...'}
                    testId="branch-select"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">Booking Type</Label>
                  <select value={newBooking.booking_type} onChange={e => setNewBooking({...newBooking, booking_type: e.target.value})} className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Columns 2 & 3 Wrapper */}
            <div className="md:col-span-2 flex flex-col gap-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                {/* Column 2: Route & Freight */}
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 space-y-3.5 h-full">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Route & Load</h3>
                    
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-600">From <span className="text-red-500">*</span></Label>
                      <MasterCombobox items={locations} value={newBooking.from_location_id} onSelect={id => {
                        const loc = locations.find(x => x.id === id);
                        setNewBooking({...newBooking, from_location_id: id || '', from_location_name: loc?.name || ''});
                      }} displayField="name" subtextField="subtext" placeholder="-- Select --" testId="from-loc" />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-600">To <span className="text-red-500">*</span></Label>
                      <MasterCombobox items={locations} value={newBooking.to_location_id} onSelect={id => {
                        const loc = locations.find(x => x.id === id);
                        setNewBooking({...newBooking, to_location_id: id || '', to_location_name: loc?.name || ''});
                      }} displayField="name" subtextField="subtext" placeholder="-- Select --" testId="to-loc" />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-600">Load Type</Label>
                      <select value={newBooking.load_type} onChange={e => setNewBooking({...newBooking, load_type: e.target.value})} className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                        <option value="">--Select--</option>
                        <option value="FTL">FTL (Full Truck Load)</option>
                        <option value="PTL">PTL (Part Truck Load)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Column 3: Vehicle Specs */}
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 space-y-3.5 h-full">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Vehicle Specs</h3>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-600">Vehicle Type</Label>
                      <select value={newBooking.vehicle_type_id} onChange={e => {
                          const t = vehicleTypes.find(x => x.id === e.target.value);
                          setNewBooking({...newBooking, vehicle_type_id: e.target.value, vehicle_type_name: t?.name || ''});
                        }} className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                        <option value="">-- Select Type --</option>
                        {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-gray-600">No. of Trucks</Label>
                          {newBooking.no_of_vehicles > 1 && (
                            <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.2 rounded font-bold">
                              {newBooking.no_of_vehicles} Trucks
                            </span>
                          )}
                        </div>
                        <Input
                          type="number"
                          min="1"
                          max="20"
                          value={newBooking.no_of_vehicles}
                          onChange={e => handleNoOfTrucksChange(e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-600">Trip Type</Label>
                        <select value={newBooking.trip_type} onChange={e => setNewBooking({...newBooking, trip_type: e.target.value})} className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                          <option value="">--Select--</option>
                          <option value="Single">Single</option>
                          <option value="Round">Round</option>
                        </select>
                      </div>
                    </div>

                    {/* Single Truck Capacity (when no_of_vehicles <= 1) */}
                    {newBooking.no_of_vehicles <= 1 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-gray-600">Size / Capacity</Label>
                        <select
                          value={availableSizes.some(s => s.size_name === newBooking.vehicle_size) ? newBooking.vehicle_size : ''}
                          onChange={e => {
                            const val = e.target.value;
                            setTruckSizes([val]);
                            setNewBooking({...newBooking, vehicle_size: val});
                          }}
                          className="flex h-8 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">-- Select Size --</option>
                          {availableSizes.map((s, idx) => (
                            <option key={s.id || idx} value={s.size_name}>{s.size_name}</option>
                          ))}
                        </select>
                        <div className="mt-1">
                          <Input
                            placeholder="Or type custom size (e.g. 32 FT)..."
                            value={newBooking.vehicle_size}
                            onChange={e => {
                              const val = e.target.value;
                              setTruckSizes([val]);
                              setNewBooking({...newBooking, vehicle_size: val});
                            }}
                            className="h-7 text-xs bg-gray-50 border-gray-200"
                          />
                        </div>
                      </div>
                    )}

                    {/* Multi-Truck Capacities (when no_of_vehicles > 1) */}
                    {newBooking.no_of_vehicles > 1 && (
                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-bold text-gray-700 font-ibm flex items-center gap-1.5">
                            <Truck className="w-3.5 h-3.5 text-blue-600" />
                            Capacities ({newBooking.no_of_vehicles} Trucks)
                          </Label>
                          {truckSizes[0] && (
                            <button
                              type="button"
                              onClick={handleCopyFirstSizeToAll}
                              className="text-[11px] text-blue-600 hover:text-blue-800 font-medium font-ibm hover:underline"
                            >
                              Same for all
                            </button>
                          )}
                        </div>

                        <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                          {Array.from({ length: newBooking.no_of_vehicles }).map((_, idx) => (
                            <div key={idx} className="p-2 bg-gray-50 rounded border border-gray-200 space-y-1">
                              <div className="flex items-center justify-between text-xs font-semibold text-gray-700 font-ibm">
                                <span>Truck #{idx + 1} Capacity</span>
                                {truckSizes[idx] && (
                                  <span className="text-[11px] font-bold text-blue-600 font-ibm">
                                    {truckSizes[idx]}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <select
                                  value={availableSizes.some(s => s.size_name === truckSizes[idx]) ? truckSizes[idx] : ''}
                                  onChange={e => handleTruckSizeChange(idx, e.target.value)}
                                  className="flex h-7 w-full rounded border border-input bg-white px-2 py-0.5 text-xs shadow-sm font-ibm"
                                >
                                  <option value="">-- Select Size --</option>
                                  {availableSizes.map((s, sIdx) => (
                                    <option key={s.id || sIdx} value={s.size_name}>{s.size_name}</option>
                                  ))}
                                </select>
                                <Input
                                  placeholder="Or custom (e.g. 17 FT)..."
                                  value={truckSizes[idx] || ''}
                                  onChange={e => handleTruckSizeChange(idx, e.target.value)}
                                  className="h-7 text-xs bg-white border-gray-200 font-ibm"
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Combined capacities pill */}
                        <div className="text-[11px] bg-blue-50 text-blue-800 p-2 rounded border border-blue-200 font-ibm">
                          <span className="font-semibold">Selected: </span>
                          <span className="font-medium">
                            {newBooking.vehicle_size || 'Please select capacities for all trucks'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Dedicated Multi-row Package & Dimensions Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider font-ibm">
                      Cargo Packages & Dimensions
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPackageRow}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium font-ibm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Package Line
                  </button>
                </div>

                <div className="space-y-2 max-h-[175px] overflow-y-auto pr-1">
                  {packageItems.map((item, idx) => (
                    <div key={item.id || idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50/80 p-2 rounded border border-gray-200">
                      <div className="col-span-3">
                        <Label className="text-[10px] text-gray-500 block mb-0.5 font-medium">No. of Packages</Label>
                        <Input
                          type="number"
                          min="1"
                          placeholder="e.g. 10"
                          value={item.packages}
                          onChange={e => handleUpdatePackageItem(idx, 'packages', e.target.value)}
                          className="h-7 text-xs bg-white"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-[10px] text-gray-500 block mb-0.5 font-medium">Gross Weight (Kg)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 500"
                          value={item.gross_weight}
                          onChange={e => handleUpdatePackageItem(idx, 'gross_weight', e.target.value)}
                          className="h-7 text-xs bg-white"
                        />
                      </div>
                      <div className="col-span-4">
                        <Label className="text-[10px] text-gray-500 block mb-0.5 font-medium">Dimension (L × W × H)</Label>
                        <Input
                          placeholder="e.g. 100 x 80 x 120"
                          value={item.dimensions}
                          onChange={e => handleUpdatePackageItem(idx, 'dimensions', e.target.value)}
                          className="h-7 text-xs bg-white"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-[10px] text-gray-500 block mb-0.5 font-medium">Unit</Label>
                        <select
                          value={item.unit}
                          onChange={e => handleUpdatePackageItem(idx, 'unit', e.target.value)}
                          className="h-7 w-full rounded border border-input bg-white px-1 text-[11px] font-ibm shadow-sm"
                        >
                          <option value="CM">CM</option>
                          <option value="Inches">IN</option>
                        </select>
                      </div>
                      <div className="col-span-1 flex items-end justify-center pt-3.5">
                        {packageItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemovePackageRow(idx)}
                            className="text-red-500 hover:text-red-700 p-1 transition-colors"
                            title="Remove this package line"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Summary Footer */}
                <div className="flex items-center justify-between bg-blue-50/70 border border-blue-100 rounded px-3 py-1.5 text-xs font-ibm">
                  <span className="font-semibold text-blue-900">Total Cargo Summary:</span>
                  <div className="flex gap-4 text-blue-800">
                    <span>Total Packages: <strong className="text-blue-950">{totalPackages}</strong></span>
                    <span>Total Gross Weight: <strong className="text-blue-950">{totalGrossWeight} Kg</strong></span>
                  </div>
                </div>
              </div>

              {/* Remarks Box placed directly under Columns 2 & 3 */}
              <div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 space-y-1.5">
                  <Label className="text-xs text-gray-600">Remarks</Label>
                  <textarea value={newBooking.remarks} onChange={e => setNewBooking({...newBooking, remarks: e.target.value})} placeholder="Special instructions..." className="flex min-h-[60px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" />
                </div>
              </div>

            </div>

          </div>

          <DialogFooter className="bg-white border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowNewBookingModal(false)} className="h-8 px-4 text-xs font-ibm">Cancel</Button>
            <Button onClick={handleSaveBooking} disabled={savingBooking} className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-6 text-xs font-ibm font-bold">
              {savingBooking ? 'Saving...' : 'Save Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default BookingDispatchBoard;
